import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaChevronDown, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
export default function RenovationFilterBar({ filter, setFilter }) {
  const [openCategories, setOpenCategories] = useState(false);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");

  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");

  const categoryRef = useRef(null);

  const categoryGroups = {
    construction_foundation: [
      "blocks_bricks",
      "cement",
      "sand",
      "gravel",
      "concrete",
      "rebar_metal",
      "metal_mesh",
      "pipes",
      "waterproofing",
      "thermal_insulation",
      "sound_insulation",
      "roofing",
      "gutters",
      "sandwich_panels",
    ],

    preparation_rough_construction: [
      "drywall_gypsum_board",
      "metal_profiles",
      "putty",
      "plaster",
      "primers",
      "self_leveling_floors",
      "tile_adhesive",
      "insulation_adhesive",
      "construction_chemicals",
      "accessories",
    ],

    engineering_utilities: [
      "electrical",
      "cables",
      "switches",
      "sockets",
      "breakers",
      "electrical_boxes",
      "conduits",

      "plumbing",
      "water_pipes",
      "sewer_pipes",
      "fittings",
      "collectors",
      "faucets",

      "heating",
      "radiators",
      "heating_pipes",
      "heating_equipment",

      "ventilation",
      "ducts",
      "grilles",
      "fans",
    ],

    finishing_interior: [
      "tiles",
      "porcelain_ceramic_tiles",
      "laminate",
      "parquet",
      "linoleum",
      "wallpaper",
      "paint",
      "decorative_plaster",
      "doors",
      "windows",
      "bathroom_fixtures",
      "bathtubs",
      "showers",
      "toilets",
      "sinks",
      "lighting",
    ],
  };

 const { t } = useTranslation();


  /* --------------------------------
     CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  -------------------------------- */

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close when interacting with language selector
      if (event.target.closest(".language-switch")) {
        return;
      }

      // Don't close when clicking inside category area
      if (
        categoryRef.current &&
        categoryRef.current.contains(event.target)
      ) {
        return;
      }

      setOpenCategories(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* --------------------------------
     FILTER CATEGORIES WHILE TYPING
  -------------------------------- */

  const filteredCategoryGroups = Object.entries(categoryGroups)
  .map(([group, items]) => {
    const search = categorySearch.toLowerCase().trim();

    // Translated group name
    const translatedGroup = t(`renovation.groups.${group}`);

    // If search matches the group name,
    // show all items in that group
    const groupMatches = translatedGroup
      .toLowerCase()
      .includes(search);

    const filteredItems = items.filter((item) => {
      const translatedItem = t(`renovation.categories.${item}`);

      return (
        translatedItem.toLowerCase().includes(search) ||
        item.toLowerCase().includes(search)
      );
    });

    if (groupMatches) {
      return [group, items];
    }

    return [group, filteredItems];
  })
  .filter(([_, items]) => items.length > 0);

  /* --------------------------------
     SELECT / UNSELECT CATEGORY
  -------------------------------- */

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((item) => item !== category);
      }

      return [...prev, category];
    });
  };


const toggleGroup = (group) => {
  setSelectedGroups((prev) => {
    if (prev.includes(group)) {
      return prev.filter((item) => item !== group);
    }

    return [...prev, group];
  });
};


const removeGroup = (group) => {
  setSelectedGroups((prev) =>
    prev.filter((item) => item !== group)
  );
};

const removeCategory = (category) => {
  setSelectedCategories((prev) =>
    prev.filter((item) => item !== category)
  );
};

  /* --------------------------------
     APPLY
  -------------------------------- */

  const handleApply = () => {
    const newFilter = {
      categories: selectedCategories,
      categoryGroups: selectedGroups,
      priceFrom,
      priceTo,
    };

    setFilter(newFilter);
    setOpenCategories(false);
  };

  return (
    <section className="renovation-search-section">

      <div className="renovation-search-bar">

        {/* =========================
            CATEGORY SEARCH
        ========================== */}

        <div
          className="renovation-category-wrapper"
          ref={categoryRef}
        >

          <div
            className="renovation-category-input-area"
            onClick={() => setOpenCategories(true)}
          >

            <div className="renovation-selected-categories">

              {/* SELECTED CATEGORY CHIPS */}

{selectedGroups.map((group) => (
  <span
    key={group}
    className="renovation-category-chip group-chip"
  >
    {t(`renovation.groups.${group}`)}

    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        removeGroup(group);
      }}
    >
      <FaTimes />
    </button>
  </span>
))}


              {selectedCategories.map((category) => (
                <span
                  key={category}
                  className="renovation-category-chip"
                >
                  {t(`renovation.categories.${category}`)}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCategory(category);
                    }}
                  >
                    <FaTimes />
                  </button>
                </span>
              ))}

              {/* SEARCH INPUT */}

              <input
                type="text"
                value={categorySearch}
                placeholder={
                  selectedCategories.length === 0
                    ? "What are you looking for?"
                    : "Add another category..."
                }
                onChange={(e) => {
                  setCategorySearch(e.target.value);
                  setOpenCategories(true);
                }}
                onFocus={() => setOpenCategories(true)}
              />

            </div>

            <FaChevronDown
              className="renovation-category-arrow"
            />

          </div>

          {/* =========================
              CATEGORY DROPDOWN
          ========================== */}

          {openCategories && (
            <div className="renovation-category-dropdown">

              {filteredCategoryGroups.length === 0 ? (

                <div className="renovation-no-results">
                  No categories found
                </div>

              ) : (

                filteredCategoryGroups.map(([group, items]) => (

                  <div
                    key={group}
                    className="renovation-category-group"
                  >

                    {/* GROUP TITLE */}

                    <button
  type="button"
  className={`renovation-category-group-title ${
    selectedGroups.includes(group) ? "selected" : ""
  }`}
  onClick={() => toggleGroup(group)}
>
  <span>
    {t(`renovation.groups.${group}`)}
  </span>

  {selectedGroups.includes(group) && (
    <span className="renovation-check">
      ✓
    </span>
  )}
</button>

                    {/* GROUP ITEMS */}

                    {items.map((item) => {

                      const selected =
                        selectedCategories.includes(item);

                      return (
                        <button
                          key={item}
                          type="button"
                          className={`renovation-category-option ${
                            selected ? "selected" : ""
                          }`}
                          onClick={() => toggleCategory(item)}
                        >

                          <span>
                            {t(`renovation.categories.${item}`)}
                          </span>

                          {selected && (
                            <span className="renovation-check">
                              ✓
                            </span>
                          )}

                        </button>
                      );
                    })}

                  </div>

                ))

              )}

            </div>
          )}

        </div>

        {/* =========================
            PRICE FROM
        ========================== */}

        <div className="renovation-price">

          <input
            type="number"
            min="0"
            placeholder="Min price"
            value={priceFrom}
            onChange={(e) =>
              setPriceFrom(e.target.value)
            }
          />

        </div>

        {/* =========================
            PRICE TO
        ========================== */}

        <div className="renovation-price">

          <input
            type="number"
            min="0"
            placeholder="Max price"
            value={priceTo}
            onChange={(e) =>
              setPriceTo(e.target.value)
            }
          />

        </div>

        {/* =========================
            SEARCH
        ========================== */}

        <button
          type="button"
          className="renovation-search-button"
          onClick={handleApply}
        >
          <FaSearch />
          Search
        </button>

      </div>

    </section>
  );
}
